import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs/internal/Subscription'
import { PageChangedEvent } from 'ngx-bootstrap/pagination'
import { SquidexService } from '../../../../services/squidex.service'
import { AccountingFirm } from '../../../../models/accountingFirm.model'
import { BehaviorSubject } from 'rxjs'
import { isScullyGenerated, TransferStateService } from '@scullyio/ng-lib'
import { take } from 'rxjs/operators'
import {
  AccountingFirmRegion,
  AccountingFirmType,
  AccountingFirmDisplayType,
} from '../../../../models/box-format.model'
import { TypeList, RegionList } from '../list'
import accountingFirms from '../content.json'

@Component({
  selector: 'flowaccount-accountingfirm-find',
  templateUrl: './find.component.html',
  styleUrls: ['./find.component.scss'],
})
export class FindComponent implements OnInit, OnDestroy {
  @Input() accountingFirmDisplayType: AccountingFirmDisplayType
  public partnerData: BehaviorSubject<Array<AccountingFirm>> = new BehaviorSubject<Array<AccountingFirm>>([])
  public accountingFirmTypeEnum = AccountingFirmType
  public accountingFirmRegionEnum = AccountingFirmRegion
  public typeList = TypeList
  public regionList = RegionList
  public currentPage: number
  public page?: number
  public totalItems: number
  public itemsPerPage: number
  public maxSizePage = 10

  public lang = this.translate.currentLang
  public accountingfirmList

  public searchName = ''

  public searchProvinces: string[] = []

  public filteredProvinces: string[] = []

  public isDropdownVisible = false

  protected selectedType: number
  protected selectedRegion: string

  private recommendDisplayItem = 100
  private partnerDisplayItem = 8

  private isApiSource = true

  private subscriptionService: Subscription[] = []
  private LIST_KEY

  constructor(
    public translate: TranslateService,
    public readonly squidexService: SquidexService,
    private cdref: ChangeDetectorRef,
    protected tstate: TransferStateService,
  ) {
    this.selectedType = this.accountingFirmTypeEnum.All
    this.selectedRegion = this.accountingFirmRegionEnum.All
  }

  ngOnInit(): void {
    this.currentPage = 1

    if (this.accountingFirmDisplayType === AccountingFirmDisplayType.Recommend) {
      this.itemsPerPage = this.recommendDisplayItem
      this.LIST_KEY = 'fullListRecommend'
    } else {
      this.itemsPerPage = this.partnerDisplayItem
      this.LIST_KEY = 'fullListPartner'
    }

    if (isScullyGenerated()) {
      this.tstate.useScullyTransferState(this.LIST_KEY, this.accountingfirmList).subscribe((data: AccountingFirm[]) => {
        this.partnerData.next(data)
        this.getAccountingfirmList()
        this.cdref.markForCheck()
      })
    } else {
      this.getAccountingFirmService()
    }

    document.addEventListener('click', this.handleClickOutside.bind(this))
  }

  ngOnDestroy() {
    this.subscriptionService.forEach((x) => x.unsubscribe())
    document.removeEventListener('click', this.handleClickOutside.bind(this))
  }

  displayText(nameTH: string, nameEN: string) {
    return this.translate.currentLang === 'th' ? nameTH : nameEN
  }

  pageChanged(event: PageChangedEvent): void {
    this.currentPage = event.page
    this.getAccountingfirmList()
  }

  onSearch() {
    this.isDropdownVisible = false
    this.currentPage = 1
    this.getAccountingfirmList()
  }

  clearSearch() {
    this.searchName = ''
    this.searchProvinces = []
    this.onSearch()
  }

  toggleDropdown() {
    this.isDropdownVisible = !this.isDropdownVisible
    if (this.isDropdownVisible) {
      this.getAccountingfirmList()
      this.resetFilteredProvinces()
    }
  }

  selectProvince(province: string) {
    this.searchProvinces = [province]
    this.isDropdownVisible = false
    this.onSearch()
  }

  clearProvince(province: string) {
    this.searchProvinces = this.searchProvinces.filter((p) => p !== province)
    this.onSearch()
  }

  protected searchAccountingFirm(type: number, region: string) {
    this.selectedType = +type
    this.selectedRegion = region
    this.currentPage = 1
    this.getAccountingfirmList()
  }

  protected getAccountingfirmList() {
    if (this.isApiSource) {
      this.accountingfirmList = this.partnerData?.value
    } else {
      this.getAccountingfirmByType()
    }
    this.filterAccountingfirm()
    this.pagination()
    this.cdref.markForCheck()
  }

  private getAccountingFirmService(): void {
    this.subscriptionService.push(
      this.squidexService
        .getAccountingFirms(this.accountingFirmDisplayType)
        .pipe(take(1))
        .subscribe((data: AccountingFirm[]) => {
          this.partnerData.next(data)
          this.getAccountingfirmList()
          this.tstate.setState(this.LIST_KEY, data)
          this.cdref.markForCheck()
        }),
    )
  }

  private getAccountingfirmByType() {
    if (this.accountingFirmDisplayType === 'recommend') {
      this.accountingfirmList = accountingFirms.recommend_accountingfirm
    } else if (this.accountingFirmDisplayType === 'partner') {
      this.accountingfirmList = accountingFirms.partner_accountingfirm
    } else {
      this.accountingfirmList = []
    }
  }

  private filterAccountingfirm() {
    if (this.selectedType !== this.accountingFirmTypeEnum.All) {
      this.accountingfirmList = this.accountingfirmList?.filter((item) => item.type === this.selectedType)
    }

    if (this.selectedRegion !== this.accountingFirmRegionEnum.All) {
      this.accountingfirmList = this.accountingfirmList?.filter((item) => item.region === this.selectedRegion)
    }

    if (this.searchName) {
      this.accountingfirmList = this.accountingfirmList?.filter(
        (item) => item.name_th.includes(this.searchName) || item.name_en.includes(this.searchName),
      )
    }

    if (this.searchProvinces.length > 0) {
      this.accountingfirmList = this.accountingfirmList?.filter((item) => {
        const province = this.translate.currentLang === 'th' ? item.province_th.trim() : item.province_en.trim()
        return this.searchProvinces.some((searchProvince) => province.includes(searchProvince))
      })
    }

    this.updateFilteredProvinces()

    this.totalItems = this.accountingfirmList?.length
  }

  private pagination() {
    const startItem = (this.currentPage - 1) * this.itemsPerPage
    const endItem = this.currentPage * this.itemsPerPage
    this.accountingfirmList = this.accountingfirmList?.slice(startItem, endItem)
  }

  private updateFilteredProvinces() {
    const provinces = this.accountingfirmList?.map((item) =>
      this.translate.currentLang === 'th' ? item.province_th.trim() : item.province_en.trim(),
    )
    this.filteredProvinces = Array.from(new Set(provinces))
  }

  private resetFilteredProvinces() {
    const provinces = this.partnerData.value.map((item) =>
      this.translate.currentLang === 'th' ? item.province_th.trim() : item.province_en.trim(),
    )
    this.filteredProvinces = Array.from(new Set(provinces))
  }

  private handleClickOutside(event: Event) {
    const target = event.target as HTMLElement
    if (!target.closest('.box-search__wrapper')) {
      this.isDropdownVisible = false
      this.cdref.detectChanges()
    }
  }
}
